/**
 * types/reference/BrokerCatalog.ts
 * Light broker catalog metadata for selection and filtering
 * 
 * Optimized for broker discovery phase - contains only essential
 * information needed for filtering, searching, and initial selection.
 * Heavy fee configuration data loaded separately when needed.
 */

import type { AssetClassType } from '@/types/shared/AssetClass';
import type { AccountType } from '@/types/shared/AccountType';


/**
 * General purpose broker account types (non-prop, non-paper)
 * These account types all use the same broker selection logic with asset-based filtering
 */
export const GENERAL_BROKER_ACCOUNT_TYPES = [
  'personal', 
  'retirement', 
  'business', 
  'trust', 
  'pooled', 
  'other'
] as const;

/**
 * Light broker metadata for selection and filtering
 * 
 * Used during onboarding and broker discovery phase.
 * Contains minimal data needed for fast filtering and display.
 */
export interface BrokerCatalogEntry {
  /** Unique broker identifier (includes context: "tradovate-personal", "tradovate-apex") */
  id: string;
  
  /** Full broker name for display */
  name: string;
  
  /** Short display name for compact views */
  shortName?: string;
  
  /** Asset classes supported by this broker context */
  assetClasses: AssetClassType[];
  
  /** Account type - can be single type (prop/paper) or array for general brokers */
  accountType: AccountType | readonly AccountType[];
  
  /** Prop firms that use this broker (for prop account filtering) */
  supportedPropFirms?: string[];
  
  /** Whether this broker should appear in "popular" lists */
  isPopular: boolean;
  
  /** Optional parent broker if this is a specialized context */
  parentBroker?: string;
  
  /** Optional notes about this broker context */
  notes?: string;
}

/**
 * Broker search and filtering options for selection phase
 */
export interface BrokerCatalogCriteria {
  /** Filter by asset class */
  assetClass?: AssetClassType;
  
  /** Filter by account type */
  accountType?: AccountType;
  
  /** Filter by prop firm support */
  propFirmId?: string;
  
  /** Filter by popular brokers only */
  popularOnly?: boolean;
  
  /** Text search in name/shortName */
  searchText?: string;
}

/**
 * Helper type for broker dropdown options in onboarding
 */
export interface BrokerCatalogOption {
  /** Broker reference ID */
  value: string;
  
  /** Display label */
  label: string;
  
  /** Whether this option is disabled */
  disabled?: boolean;
  
  /** Additional info for tooltips */
  description?: string;
  
  /** Whether this is a separator/header */
  isSeparator?: boolean;
  
  /** Optional grouping for categorized display */
  group?: string;
}

/**
 * Type guard to check if broker supports prop firms
 */
export const supportsPropFirms = (broker: BrokerCatalogEntry): broker is BrokerCatalogEntry & { supportedPropFirms: string[] } => {
  return broker.accountType === 'prop' && Array.isArray(broker.supportedPropFirms) && broker.supportedPropFirms.length > 0;
};

/**
 * Helper to check if broker supports specific prop firm
 */
export const supportsPropFirm = (broker: BrokerCatalogEntry, propFirmId: string): boolean => {
  return supportsPropFirms(broker) && broker.supportedPropFirms.includes(propFirmId);
};

/**
 * Helper to check if broker supports specific asset class
 */
export const supportsAssetClass = (broker: BrokerCatalogEntry, assetClass: AssetClassType): boolean => {
  return broker.assetClasses.includes(assetClass);
};